import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import Model, { Providers } from "@tripian/model";
import { providers } from "@tripian/core";
import { saveNotification } from "../redux/action/user";

const useVictoryInfo = () => {
  const [loadingEventDetails, setLoadingEventDetails] =
    useState<boolean>(false);
  const [loadingVenueData, setLoadingVenueData] = useState<boolean>(false);
  const [loadingTicketGroups, setLoadingTicketGroups] =
    useState<boolean>(false);
  const [loadingCreateOrder, setLoadingCreateOrder] = useState<boolean>(false);
  const [eventDetails, setEventDetails] =
    useState<Providers.Victory.EventDetail>();
  const [venueData, setVenueData] = useState<Providers.Victory.Venue>();
  const [ticketGroups, setTicketGroups] =
    useState<Providers.Victory.TicketGroup[]>();

  const dispatch = useDispatch();

  const fetchEventDetails = useCallback(
    async (id: string): Promise<Providers.Victory.EventDetail | null> => {
      if (!providers.victory || !window.tconfig.SHOW_TOURS_AND_TICKETS) {
        return null;
      }

      setLoadingEventDetails(true);
      try {
        const eventData = await providers.victory.eventDetails(id);
        setEventDetails(eventData);
        return eventData;
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "victoryFetchEventError",
            error as any
          )
        );
        return null;
      } finally {
        setLoadingEventDetails(false);
      }
    },
    [dispatch]
  );

  const fetchVenueData = useCallback(
    async (venueId: string): Promise<Providers.Victory.Venue | null> => {
      if (!providers.victory || !window.tconfig.SHOW_TOURS_AND_TICKETS) {
        return null;
      }

      setLoadingVenueData(true);
      try {
        const venueData = await providers.victory.venue(venueId);
        setVenueData(venueData);
        return venueData;
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "victoryFetchVenueError",
            error as any
          )
        );
        return null;
      } finally {
        setLoadingVenueData(false);
      }
    },
    [dispatch]
  );

  const fetchTicketGroups = useCallback(
    async (id: string): Promise<Providers.Victory.TicketGroup[] | null> => {
      if (!providers.victory || !window.tconfig.SHOW_TOURS_AND_TICKETS) {
        return null;
      }

      setLoadingTicketGroups(true);
      try {
        const listTicketGroup = await providers.victory.listTicketGroups(id);
        const filteredTicketGroup = listTicketGroup.ticket_groups.filter(
          t => t.eticket === true
        );
        setTicketGroups(filteredTicketGroup);
        return filteredTicketGroup;
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "victoryFetchTicketsError",
            error as any
          )
        );
        return null;
      } finally {
        setLoadingTicketGroups(false);
      }
    },
    [dispatch]
  );

  const createOrder = useCallback(
    async (
      orderData: Providers.Victory.OrderRequest
    ): Promise<Providers.Victory.OrdersResponse | null> => {
      if (!providers.victory || !window.tconfig.SHOW_TOURS_AND_TICKETS) {
        return null;
      }

      setLoadingCreateOrder(true);
      try {
        const order = await providers.victory.createOrder(orderData);
        return order;
      } catch (error) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "victoryCreateOrderError",
            error as any
          )
        );
        return null;
      } finally {
        setLoadingCreateOrder(false);
      }
    },
    [dispatch]
  );

  return {
    fetchEventDetails,
    fetchVenueData,
    fetchTicketGroups,
    createOrder,
    loadingEventDetails,
    loadingVenueData,
    loadingTicketGroups,
    loadingCreateOrder,
    eventDetails,
    venueData,
    ticketGroups,
  };
};

export default useVictoryInfo;
